/**
 * Utility to translate notification texts based on their type.
 *
 * Notification texts are stored as pre-formatted English strings in the
 * database. This utility parses those strings using known patterns and returns
 * translated equivalents via i18next.
 *
 * Fallback: if no pattern matches, the original text is returned unchanged.
 */

/**
 * Each entry maps a notification type to one or more matchers.
 * Multiple matchers per type handle different text variants for the same type.
 *
 * matcher:
 *   regex  – pattern to test against the stored text
 *   key    – i18next translation key (coaching-portal namespace)
 *   params – array of parameter names; the i-th name maps to match group i+1
 */
const PATTERNS = {
  CheckIn: [
    {
      regex: /^(.+) completed a check-in on (.+)$/,
      key: "notifications.texts.checkInCompleted",
      params: ["name", "date"],
    },
  ],

  "Coach Feedback": [
    {
      regex: /^(.+) sent you feedback!$/,
      key: "notifications.texts.feedbackSentViewInApp",
      params: ["name"],
    },
  ],

  OnboardingQuestionnaire: [
    {
      regex: /^(.+) completed the onboarding questionnaire$/,
      key: "notifications.texts.onboardingCompleted",
      params: ["name"],
    },
  ],

  Push: [
    // "{name} added a video to their {date} workout."
    {
      regex: /^(.+) added a video to their (.+) workout\.$/,
      key: "notifications.texts.videoAddedWorkout",
      params: ["name", "date"],
    },
    // "{name} added a note to their {date} workout, {note}"
    {
      regex: /^(.+) added a note to their (.+) workout, (.+)$/,
      key: "notifications.texts.noteAddedWorkout",
      params: ["name", "date", "note"],
    },
    // "Your coach has pushed a training program to you." (athlete-side Push)
    {
      regex: /^Your coach has pushed a training program to you\.$/,
      key: "notifications.texts.programPushed",
      params: [],
    },
  ],

  "CheckIn Note": [
    // "{name} added a note to their {date} check-in, {note}"
    {
      regex: /^(.+) added a note to their (.+) check-in, (.+)$/,
      key: "notifications.texts.noteAddedCheckIn",
      params: ["name", "date", "note"],
    },
  ],

  "Coach Note": [
    // "Your coach left a comment on your {date} workout, {note}"
    {
      regex: /^Your coach left a comment on your (.+) workout, (.+)$/,
      key: "notifications.texts.coachNoteWorkout",
      params: ["date", "note"],
    },
    // "Your coach left a comment on your {date} check-in report, {note}"
    {
      regex: /^Your coach left a comment on your (.+) check-in report, (.+)$/,
      key: "notifications.texts.coachNoteCheckIn",
      params: ["date", "note"],
    },
  ],

  NutritionPlan: [
    {
      regex: /^Your coach created a new nutrition plan for you ranging from (.+) to (.+)$/,
      key: "notifications.texts.nutritionPlan",
      params: ["start", "end"],
    },
  ],

  VideoRecording: [
    {
      regex: /^Your coach added a video recording\.$/,
      key: "notifications.texts.videoRecording",
      params: [],
    },
  ],

  "VideoRecording-general": [
    {
      regex: /^Your coach added a video recording to (.+)$/,
      key: "notifications.texts.videoRecordingGeneral",
      params: ["date"],
    },
  ],

  "VideoRecording-calendar": [
    {
      regex: /^Your coach added a video recording to (.+) workout\.$/,
      key: "notifications.texts.videoRecordingCalendar",
      params: ["date"],
    },
  ],

  "VideoRecording-check-in": [
    {
      regex: /^Your coach added a video recording to (.+) check-in\.$/,
      key: "notifications.texts.videoRecordingCheckIn",
      params: ["date"],
    },
  ],

  Custom: [
    // "Welcome to MyStrengthBook For Athletes! You are now being coached by {coachName}. ..."
    {
      regex: /^Welcome to MyStrengthBook For Athletes! You are now being coached by (.+)\. .+$/,
      key: "notifications.texts.welcomeAthlete",
      params: ["coachName"],
    },
  ],

  "Free Trial": [
    {
      regex: /^We hope you're enjoying the MyStrengthBook free trial - You have (\d+) days? left! .+$/,
      key: "notifications.texts.freeTrialDaysLeft",
      params: ["days"],
    },
    {
      regex: /^We hope you've enjoyed the MyStrengthBook free trial\. Today is your last day! .+$/,
      key: "notifications.texts.freeTrialLastDay",
      params: [],
    },
    {
      regex: /^Your free trial is now over\. .+$/,
      key: "notifications.texts.freeTrialOver",
      params: [],
    },
  ],

  Metrics: [
    {
      regex: /^You've just completed 8 logged workouts\. .+$/,
      key: "notifications.texts.metrics8Workouts",
      params: [],
    },
  ],

  "Social Sharing": [
    {
      regex: /^When you're finished building your first workout .+$/,
      key: "notifications.texts.socialSharing",
      params: [],
    },
  ],

  Congratulations: [
    {
      regex: /^Congratulations! You have reached a new milestone of (.+) of volume!$/,
      key: "notifications.texts.congratsMilestone",
      params: ["milestone"],
    },
  ],

  "Friend Referral": [
    {
      regex: /^All five of the friends you referred have signed up .+$/,
      key: "notifications.texts.friendReferralFive",
      params: [],
    },
    {
      // "… who you referred has signed up … extended your trial by one week! "
      regex: /^(.+) who you referred has signed up .+ extended your trial .+$/,
      key: "notifications.texts.friendReferralOne",
      params: ["name"],
    },
    {
      // "… who you referred has signed up for MyStrengthBook!"
      regex: /^(.+) who you referred has signed up for MyStrengthBook!$/,
      key: "notifications.texts.friendReferralSignup",
      params: ["name"],
    },
  ],

  "Friend Referral Suggestion": [
    {
      regex: /^Refer your friends today .+$/,
      key: "notifications.texts.friendReferralSuggestion",
      params: [],
    },
  ],

  "Facebook Group Reccomendation": [
    {
      regex: /^Join our members only Facebook Group$/,
      key: "notifications.texts.facebookGroup",
      params: [],
    },
  ],

  "User Engagement": [
    {
      regex: /^You've gone 7 days without logging a workout! .+$/,
      key: "notifications.texts.userEngagement",
      params: [],
    },
  ],
};

/**
 * Translate a notification text string.
 *
 * @param {string} text          - The raw notification text stored in the database.
 * @param {string} type          - The notification type (e.g. "CheckIn", "Push").
 * @param {Function} t           - The i18next `t` function from `useTranslation`.
 * @param {string} [requesterName] - Current display name of the requester. When
 *   provided, substituted for any "name" param so the UI always shows the
 *   up-to-date name rather than the name baked into the stored text.
 * @param {boolean} [isAthlete]  - When true the viewing user is an athlete; name
 *   replacement is skipped and the stored text is returned as-is (matching the
 *   original checkNameOnText behaviour).
 * @returns {string} The translated string, or the original `text` if no
 *                   pattern matches.
 */
export function translateNotification(
  text,
  type,
  t,
  requesterName,
  isAthlete = false,
) {
  if (!text || !type) return text;

  // When the viewer is an athlete, preserve the text exactly as stored.
  if (isAthlete) return text;

  const matchers = PATTERNS[type];
  if (!matchers) return text;

  for (const matcher of matchers) {
    const match = matcher.regex.exec(text);
    if (!match) continue;

    const params = {};
    matcher.params.forEach((param, i) => {
      // Use the live requesterName for "name" params so a renamed user is
      // always shown with their current name, mirroring checkNameOnText.
      params[param] =
        param === "name" && requesterName ? requesterName : match[i + 1];
    });

    return t(matcher.key, params);
  }

  return text;
}
